/**
 * Collaborator referral attribution — theme-wide.
 *
 * Loaded from layout/theme.liquid so a collaborator's link works no matter
 * where it points: the landing page, a product, or a collection.
 *
 *   Organic: /pages/ostrovsky?ref=ostrovsky&utm_source=instagram&utm_medium=organic_social
 *   Paid:    ...?ref=ostrovsky&utm_source=...&utm_medium=paid_social&utm_campaign=...&utm_content=...
 *
 * Model: FIRST TOUCH + LAST TOUCH, 30-day window measured from the first
 * touch. The first ref a visitor arrives with is never overwritten inside the
 * window; every later ref arrival replaces the last touch and bumps a counter.
 * The first-touch channel sets the payout rate; the last touch is evidence.
 *
 * Channel: `paid` when utm_medium looks like paid media, otherwise `organic`.
 * fbclid is deliberately ignored — Instagram appends it to organic clicks too.
 *
 * Storage: written to BOTH a first-party cookie and localStorage, and read
 * from both, so clearing one doesn't lose the ref.
 *
 * The only theme-side mechanism that reaches an order is a cart attribute,
 * so the record is written to the cart via /cart/update.js. It then lands in
 * the order's note_attributes — visible in Admin under "Additional details"
 * and included in order CSV exports. Dawn's Buy-it-now button skips the cart
 * (and so the attributes), which is why attributed visitors get the
 * `collab-attributed` class on <html> and the button is hidden by CSS.
 *
 * KNOWN CEILING: this is best-effort, not an accounting system. Safari/iOS
 * ITP caps script-written storage and cookies at 7 days — and at 24 hours
 * when the visitor arrives from Instagram/Facebook with a decorated URL.
 * Shopify carts expire after ~14 days of inactivity. Cross-device journeys
 * are invisible. Discount codes remain the authoritative payout record.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'obsidian_attribution';
  var COOKIE_NAME = 'obsidian_attr';
  var SESSION_KEY = 'obsidian_attribution_synced';
  var WINDOW_DAYS = 30;
  var WINDOW_MS = WINDOW_DAYS * 24 * 60 * 60 * 1000;
  var DEDUPE_MS = 30 * 60 * 1000;
  var PAID_MEDIUM = /paid|cpc|ppc|cpm|ads?$/i;

  // Accepted inbound parameter names, in priority order.
  var REF_PARAMS = ['ref', 'aff', 'collab'];
  var UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  function classify(medium) {
    return medium && PAID_MEDIUM.test(medium) ? 'paid' : 'organic';
  }

  /* ---- Record shape --------------------------------------------------
     { v: 2, first: Touch, last: Touch, touches: n }
     Touch = { ref, channel, ts, landing, utm_source … utm_term }
     v1 records ({ ref, ts, landing, utm_* }) are migrated on read. */
  function normalize(record) {
    if (!record || typeof record !== 'object') return null;

    if (record.v !== 2) {
      if (!record.ref || !record.ts) return null;
      var touch = { ref: record.ref, ts: record.ts, landing: record.landing || '/' };
      UTM_PARAMS.forEach(function (key) {
        if (record[key]) touch[key] = record[key];
      });
      touch.channel = classify(touch.utm_medium);
      record = { v: 2, first: touch, last: touch, touches: 1 };
    }

    if (!record.first || !record.first.ref || !record.first.ts) return null;
    if (!record.last || !record.last.ref) record.last = record.first;
    if (!record.touches) record.touches = 1;
    return record;
  }

  function isLive(record, now) {
    return record && now - record.first.ts <= WINDOW_MS;
  }

  function readCookie() {
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)'));
      return match ? JSON.parse(decodeURIComponent(match[1])) : null;
    } catch (e) {
      return null;
    }
  }

  function readLocal() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    } catch (e) {
      return null;
    }
  }

  function writeStore(value) {
    var json = JSON.stringify(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, json);
    } catch (e) {
      /* Private mode or storage full — the cookie may still hold it. */
    }
    try {
      document.cookie =
        COOKIE_NAME + '=' + encodeURIComponent(json) +
        '; path=/; max-age=' + WINDOW_DAYS * 86400 + '; SameSite=Lax' +
        (window.location.protocol === 'https:' ? '; Secure' : '');
    } catch (e) {
      /* no-op */
    }
  }

  function clearStore() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* no-op */
    }
    try {
      document.cookie = COOKIE_NAME + '=; path=/; max-age=0; SameSite=Lax';
    } catch (e) {
      /* no-op */
    }
  }

  /**
   * Reads both stores, keeps the live record with the older first touch, and
   * heals whichever store is missing or behind.
   */
  function readStore(now) {
    var rawCookie = readCookie();
    var rawLocal = readLocal();
    var fromCookie = normalize(rawCookie);
    var fromLocal = normalize(rawLocal);
    if (!isLive(fromCookie, now)) fromCookie = null;
    if (!isLive(fromLocal, now)) fromLocal = null;

    var record = fromCookie;
    if (fromLocal && (!record || fromLocal.first.ts < record.first.ts)) record = fromLocal;
    if (record && fromCookie && fromLocal && record.first.ts === fromCookie.first.ts &&
        record.first.ts === fromLocal.first.ts) {
      // Same first touch in both: take the fresher last touch / higher count.
      record = (fromLocal.last.ts || 0) >= (fromCookie.last.ts || 0) ? fromLocal : fromCookie;
    }

    if (!record) {
      // Expired or junk in either store — drop it so a stale ref never earns.
      if (rawCookie || rawLocal) clearStore();
      return null;
    }

    var json = JSON.stringify(record);
    if (JSON.stringify(rawCookie) !== json || JSON.stringify(rawLocal) !== json) writeStore(record);
    return record;
  }

  function sessionSynced() {
    try {
      return window.sessionStorage.getItem(SESSION_KEY) === JSON.stringify(buildAttributes());
    } catch (e) {
      return false;
    }
  }

  function markSessionSynced(attributes) {
    try {
      window.sessionStorage.setItem(SESSION_KEY, attributes);
    } catch (e) {
      /* no-op */
    }
  }

  var params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (e) {
    return;
  }

  function param(name) {
    var value = params.get(name);
    if (!value) return '';
    // Refs are used as cart attribute values and appear in the admin, so keep
    // them boring: strip anything that isn't a safe slug character.
    return value.trim().slice(0, 64).replace(/[^\w .@-]/g, '');
  }

  var incomingRef = '';
  for (var i = 0; i < REF_PARAMS.length; i++) {
    incomingRef = param(REF_PARAMS[i]);
    if (incomingRef) break;
  }

  var now = Date.now();
  var stored = readStore(now);

  var isNewCapture = false;
  var lastChanged = false;

  if (incomingRef) {
    var touch = {
      ref: incomingRef,
      ts: now,
      landing: window.location.pathname.slice(0, 200)
    };
    UTM_PARAMS.forEach(function (key) {
      var value = param(key);
      if (value) touch[key] = value;
    });
    touch.channel = classify(touch.utm_medium);

    if (!stored) {
      stored = { v: 2, first: touch, last: touch, touches: 1 };
      isNewCapture = true;
    } else {
      var last = stored.last;
      // A reload of the same link shouldn't count as a second touch.
      var isRepeat =
        last.ref === touch.ref &&
        UTM_PARAMS.every(function (key) { return (last[key] || '') === (touch[key] || ''); }) &&
        now - (last.ts || 0) < DEDUPE_MS;
      if (!isRepeat) {
        stored.last = touch;
        stored.touches += 1;
        lastChanged = true;
      }
    }

    if (isNewCapture || lastChanged) writeStore(stored);
  }

  if (!stored) return;

  // Lets CSS hide the Buy-it-now button, which bypasses cart attributes.
  document.documentElement.classList.add('collab-attributed');

  function day(ts) {
    return new Date(ts).toISOString().slice(0, 10);
  }

  /**
   * Cart attributes are deliberately human-readable rather than underscore-
   * prefixed, so they show up in the Admin order view and in CSV exports
   * without anyone needing to query the API. Keys match v1 for the first
   * touch so older orders and exports still line up.
   *
   * Every key is always present; missing values are '' so a later sync
   * clears a stale value (Shopify deletes blank attributes).
   */
  function buildAttributes() {
    var first = stored.first;
    var last = stored.last;
    var attributes = {
      Referral: first.ref,
      'Referral channel': first.channel,
      'Referral date': day(first.ts),
      'Referral landing': first.landing || '/'
    };
    UTM_PARAMS.forEach(function (key) {
      attributes[key] = first[key] || '';
    });
    attributes['Last referral'] = last.ref;
    attributes['Last referral channel'] = last.channel;
    attributes['Last referral date'] = day(last.ts || first.ts);
    attributes['Last utm_campaign'] = last.utm_campaign || '';
    attributes['Last utm_content'] = last.utm_content || '';
    attributes['Referral touches'] = String(stored.touches);
    return attributes;
  }

  function visibleAttributes() {
    var all = buildAttributes();
    var out = {};
    for (var key in all) {
      if (all[key]) out[key] = all[key];
    }
    return out;
  }

  function syncToCart() {
    var url =
      (window.routes && window.routes.cart_update_url) || '/cart/update.js';

    var attributes = buildAttributes();
    var syncKey = JSON.stringify(attributes);
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ attributes: attributes })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Attribution cart update failed');
        markSessionSynced(syncKey);
      })
      .catch(function () {
        /* Never let attribution break the page. */
      });
  }

  // Re-sync once per session as well as on capture / new last touch: the cart
  // can be recreated (expiry, "clear cart"), which would otherwise silently
  // drop the attributes.
  if (isNewCapture || lastChanged || !sessionSynced()) syncToCart();

  // Custom dataLayer hook; no pixel initialization (see ARCHITECTURE.md §9).
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'collab_referral',
    collab_ref: stored.first.ref,
    collab_channel: stored.first.channel,
    collab_last_channel: stored.last.channel,
    collab_touches: stored.touches,
    collab_ref_age_days: Math.floor((now - stored.first.ts) / 86400000),
    collab_ref_new: isNewCapture
  });

  // Exposed so the landing page (and anyone debugging in console) can read it.
  window.ObsidianAttribution = {
    ref: stored.first.ref,
    channel: stored.first.channel,
    capturedAt: stored.first.ts,
    first: stored.first,
    last: stored.last,
    touches: stored.touches,
    attributes: visibleAttributes(),
    resync: syncToCart
  };
})();
